/**
 * @file     useReveal.ts
 * @purpose  Scroll-into-view reveal primitive for the detail-page cards. Returns
 *           a ref + `shown` (true once the element scrolls into view, once) and
 *           `reduce` (honours prefers-reduced-motion — shown immediately, no
 *           keyframes). Transform/opacity only; no layout shift.
 */
'use client';

import * as React from 'react';

export interface Reveal {
  ref: React.RefObject<HTMLDivElement | null>;
  shown: boolean;
  reduce: boolean;
}

export function useReveal(): Reveal {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = React.useState(false);
  const [reduce, setReduce] = React.useState(false);

  React.useEffect(() => {
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    setReduce(prefersReduced);
    const el = ref.current;
    if (prefersReduced || !el || typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return { ref, shown, reduce };
}
